import { useBlocNext } from "@blac/react";
import { ProgressIndicator } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPartnerConfigByFunnelKey,
  resolvePartnerEntryGate
} from "src/constants/partnerConfig";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import UserCubit from "src/state/UserCubit/UserCubit";
import { useBloc } from "src/state/state";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import Loader from "src/ui/components/Loader/Loader";
import type { SignupCustomPageParameters } from "src/ui/components/SignupCustomContent/SignupCustom.types";
import PartnerSsoConnect from "src/ui/components/SignupCustomContent/PartnerSsoConnect";
import SignupCustomCampaign from "src/ui/components/SignupCustomContent/SignupCustomCampaign";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import AppAccessCubit, {
  AppAccessStatus
} from "src/state/AppAccessCubit/AppAccessCubit";
import { CenterEl, ErrorPageLayout } from "../SentryBoundary/ErrorPage";
import Translate from "../Translate/Translate";
import { NON_BREAKING_SPACE } from "src/constants/misc";

const ContentWrap = styled.div`
  label: ContentWrap;
  /* flex, not grid: Safari never shrinks a grid row back after a tall question */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  --p-top: calc(76px);
  --p-bottom: 2rem;
  /* min-height, not height: centered overflow above the top can't be scrolled to */
  min-height: 100svh;
  padding: var(--p-top) 0 0 0;
  box-sizing: border-box;

  & > nine-bg {
    flex: 1;
  }

  /* flex-grow, not height: 100% — percentages don't resolve under min-height */
  & > .bg-wrapper,
  & > .fade-wrap,
  & > .fade-wrap > .bg-wrapper {
    flex: 1 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* Extra step height/bottom space makes Safari bounce-scroll questions that fit */
  --questionnaire-bottom-space: 0;
  --question-bottom-space: 1rem;

  nine-funnel-step {
    --funnel-step-min-height: 0;
    --funnel-step-padding: 4rem var(--space-md) 0;
  }
`;

/* Replaces IonContent, whose inner scroller's 1px rounding bounce-scrolled in Safari */
const ScrollHost = styled.div`
  label: ScrollHost;
  background-color: var(--color-cream);
  /* height, not min-height: must stay one viewport tall for overflow-y to scroll */
  height: 100vh;
  height: 100svh;
  overflow-y: auto;
  overscroll-behavior-y: contain;

  padding-bottom: var(--keyboard-height, 0px);
`;

const SavingOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background-color: var(--color-cream);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const SAVING_TIMEOUT_MS = 20_000;

const SignupCustomContent: FC = () => {
  const navigate = useNavigate();
  const parameters = useParams<SignupCustomPageParameters>();
  const [{ status: appAccessStatus }] = useBlocNext(AppAccessCubit);
  const [
    {
      campaign,
      step,
      currentStepValid,
      isAvailable,
      savingIndicator,
      transitionPending
    },
    {
      currentStep,
      campaignNameIsValid,
      campaignReleaseDate,
      campaignPrettyName,
      isDemoFunnel,
      getActiveCampaign,
      hideSaving
    }
  ] = useBlocNext(SignupCustomBloc);
  const { sessionRestoreSettled, authenticationStatus } = useUserAuthStage();
  const [{ userData }] = useBloc(UserCubit);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [loadingState, { isLoading }] = useBlocNext(LoadingCubit);
  const checkingStep = useMemo(
    () => isLoading(LoadingKey.checkCustomSignupStepDataComplete),
    [loadingState]
  );

  const isSavingVisible = Boolean(savingIndicator ?? transitionPending);

  const partnerEntryGate = resolvePartnerEntryGate({
    funnelKey: parameters.campaign,
    sessionRestoreSettled,
    isAuthenticated: authenticationStatus === "authenticated",
    userDataLoaded: Boolean(userData),
    sessionAuthenticationMethod: userData?.sessionAuthenticationMethod,
    hasAppAccess: appAccessStatus === AppAccessStatus.GRANTED
  });
  const isPartnerEntryGated = partnerEntryGate !== "none";
  const ssoConnectPartner =
    partnerEntryGate === "connect"
      ? getPartnerConfigByFunnelKey(parameters.campaign)
      : undefined;

  useEffect(() => {
    if (partnerEntryGate === "home") {
      navigate("/app/home", { replace: true });
    }
  }, [partnerEntryGate]);

  useEffect(() => {
    if (!isSavingVisible) {
      return;
    }

    const timeoutId = setTimeout(() => {
      reportErrorSentry(
        new Error("Funnel saving indicator timeout after 20 seconds"),
        {
          campaign,
          step,
          savingIndicatorText: savingIndicator?.text?.toString() ?? null,
          transitionPending
        }
      );
      window.location.reload();
    }, SAVING_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isSavingVisible]);

  const displayReleaseDate = useMemo(() => {
    if (!campaignReleaseDate) {
      return "";
    }
    return campaignReleaseDate
      .toDate()
      .toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      })
      .split(" ")
      .join(NON_BREAKING_SPACE);
  }, [campaignReleaseDate]);

  const textContext = getActiveCampaign().availabilityChecks?.textContext ?? "";

  // redirect when step changes
  useEffect(() => {
    if (!currentStep) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const queryString = params.toString();
    const path = `${isDemoFunnel ? "/demo" : ""}/signup/${campaign}/${step}?${queryString}`;

    if (step === parameters.step) {
      return;
    }

    navigate(path, {
      replace: !parameters.step
    });
  }, [currentStep, parameters, isDemoFunnel]);

  useEffect(() => {
    if (scrollRef.current) {
      window.nineConfig = {
        ...window.nineConfig,
        mainScrollTarget: scrollRef.current
      };
    }
    return () => {
      window.nineConfig = {
        ...window.nineConfig,
        mainScrollTarget: document.body
      };
    };
  }, []);

  useEffect(() => {
    if (!campaignNameIsValid) {
      const params = new URLSearchParams(window.location.search);
      const queryString = params.toString();
      window.location.replace(`/signup/universal?${queryString}`);
    }
  }, [campaignNameIsValid]);

  return (
    <ScrollHost ref={scrollRef}>
      <ContentWrap>
        <Helmet>
          {currentStep && (
            <title>
              {translate("page_name")} - {campaignPrettyName} -{" "}
              {currentStep.name}
            </title>
          )}
        </Helmet>

        {ssoConnectPartner && <PartnerSsoConnect partner={ssoConnectPartner} />}
        {!ssoConnectPartner &&
          (partnerEntryGate === "pending" ||
            partnerEntryGate === "home" ||
            checkingStep ||
            typeof isAvailable === "undefined") && (
            <Loader fixed overContent background fullPage />
          )}
        {!isPartnerEntryGated && currentStepValid && isAvailable === false && (
          <ErrorPageLayout>
            <div>
              <CenterEl>
                <nine-heading>
                  <nine-spacer s="xs"></nine-spacer>
                  <h4 className="">
                    <Translate
                      msg="funnel.unavailable.title"
                      variables={{
                        context: textContext
                      }}
                    />
                  </h4>
                  <nine-spacer s="md"></nine-spacer>
                  <p className="m0 color-c-60">
                    <Translate
                      msg="funnel.unavailable.description"
                      variables={{
                        availableDate: displayReleaseDate,
                        context: textContext
                      }}
                    />
                  </p>
                </nine-heading>
              </CenterEl>
            </div>
          </ErrorPageLayout>
        )}

        {!isPartnerEntryGated && currentStepValid && isAvailable === true && (
          <SignupCustomCampaign />
        )}
      </ContentWrap>

      <AnimatePresence>
        {(savingIndicator ?? transitionPending) && (
          <SavingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ProgressIndicator
              indeterminate
              complete={savingIndicator?.complete && !transitionPending}
              onComplete={hideSaving}
              loadTime={savingIndicator?.durationMs ?? undefined}
              img={savingIndicator?.img}
            >
              <p className="as-subhead2 color-c-80">
                {savingIndicator?.text ?? (
                  <Translate msg="savingIndicator.info" />
                )}
              </p>
            </ProgressIndicator>
          </SavingOverlay>
        )}
      </AnimatePresence>
    </ScrollHost>
  );
};

export default SignupCustomContent;
